import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "@/lib/caixaToast";
import type { RelatorioDiaCompleto } from "./types";
import { imprimirRelatorioDia } from "./printHtml";
import { enviarRelatorioPorEmail, fetchRelatorioDia } from "@caixa/lib/api";
import { config } from "@caixa/config";

interface RelatorioGerarModalProps {
  open: boolean;
  onClose: () => void;
  /** Fecha este modal e o modal principal de relatório (após ação concluída). */
  onCloseAll?: () => void;
  report: RelatorioDiaCompleto | null;
  /** Enquanto o relatório principal ainda carrega */
  loadingReport: boolean;
  /** Atualiza o painel principal com os dados recém-lidos antes de enviar o e-mail */
  onReportUpdated?: (r: RelatorioDiaCompleto) => void;
}

export function RelatorioGerarModal({
  open,
  onClose,
  onCloseAll,
  report,
  loadingReport,
  onReportUpdated,
}: RelatorioGerarModalProps) {
  const [emailSending, setEmailSending] = useState(false);
  const emailSendingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      emailSendingRef.current = false;
      setEmailSending(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !emailSending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, emailSending]);

  const closeAll = useCallback(() => {
    if (onCloseAll) onCloseAll();
    else onClose();
  }, [onCloseAll, onClose]);

  const handlePrint = useCallback(async () => {
    if (!report || loadingReport) return;
    try {
      await imprimirRelatorioDia(report, config.empresa.nome);
      toast.success("Impressão iniciada");
      closeAll();
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível abrir a impressão. Verifique o bloqueio de pop-ups.");
    }
  }, [report, loadingReport, closeAll]);

  const handleEmail = useCallback(async () => {
    if (!report || loadingReport || emailSendingRef.current) return;
    emailSendingRef.current = true;
    setEmailSending(true);
    try {
      const fresh = await fetchRelatorioDia(report.date);
      onReportUpdated?.(fresh);
      const { emails } = await enviarRelatorioPorEmail(fresh.date);
      toast.success(`Relatório enviado com sucesso para: ${emails.join(", ")}`);
      closeAll();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Não foi possível enviar o e-mail.";
      toast.error(msg);
    } finally {
      emailSendingRef.current = false;
      setEmailSending(false);
    }
  }, [report, loadingReport, onReportUpdated, closeAll]);

  if (!open) return null;

  const busy = loadingReport || !report;
  const disabledActions = busy || emailSending;

  return (
    <div
      className="caixa-overlay caixa-overlay--60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="relatorio-gerar-title"
    >
      <div className="caixa-modal">
        <div className="caixa-modal-head">
          <h2 id="relatorio-gerar-title" className="caixa-modal-title">
            Gerar relatório diário
          </h2>
          <button
            type="button"
            onClick={() => !emailSending && onClose()}
            disabled={emailSending}
            className="caixa-modal-close"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <p className="caixa-modal-sub">
          Escolha como deseja receber o relatório do dia
        </p>
        {busy && (
          <p className="caixa-modal-sub">Carregando dados do relatório…</p>
        )}
        <div className="caixa-modal-footer">
          <button
            type="button"
            onClick={() => void handlePrint()}
            disabled={disabledActions}
            className="caixa-btn-outline"
          >
            Imprimir relatório
          </button>
          <button
            type="button"
            onClick={() => void handleEmail()}
            disabled={disabledActions}
            className="caixa-btn-primary"
          >
            {emailSending ? "Enviando…" : "Receber por email"}
          </button>
        </div>
      </div>
    </div>
  );
}
